import React from "react";
import { AlertTriangle, ShieldCheck, Bug, Globe, FileText } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import ActionPlanIssuesPanel from "../ActionPlanIssuesPanel";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenTechnicalIssues, flattenTopPages, flattenMetrics } from "../../reports/exportDataUtils";

interface TechnicalTabContentProps {
  liveData?: any | null;
  activeSubNav?: string;
  cardId?: string;
}

const severityColor = (sev: string) => {
  const normalized = (sev || "").toUpperCase();
  if (normalized === "HIGH" || normalized === "CRITICAL") return { bg: "rgba(248,113,113,0.15)", text: "#F87171", border: "rgba(248,113,113,0.3)" };
  if (normalized === "LOW" || normalized === "INFO") return { bg: "rgba(74,222,128,0.15)", text: "#4ADE80", border: "rgba(74,222,128,0.3)" };
  return { bg: "rgba(255,154,74,0.15)", text: "#FF9A4A", border: "rgba(255,154,74,0.3)" };
};

const scoreColor = (score: number) => {
  if (score >= 80) return "#4ADE80";
  if (score >= 60) return "#FF9A4A";
  return "#F87171";
};

const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const filterMap: Record<string, string[]> = {
  ALL: [],
  CRITICAL: ["CRITICAL"],
  WARNINGS: ["WARNING", "MED"],
  INFO: ["INFO", "LOW"],
};


const TechnicalTabContent: React.FC<TechnicalTabContentProps> = ({ liveData, activeSubNav, cardId = "search-intelligence" }) => {
  const techSEO = liveData?.technicalSEO ?? null;
  const issues: any[] | null = techSEO?.issues?.length ? techSEO.issues : null;
  const [activeFilter, setActiveFilter] = React.useState("ALL");

  const filteredIssues = React.useMemo(() => {
    if (!issues) return [];

    if (activeFilter === "ALL") return issues;

    return issues.filter((issue) =>
      filterMap[activeFilter].includes((issue.severity || "").toUpperCase())
    );
  }, [issues, activeFilter]);

  // ─── SUB-NAV: Page Audit ───
  if (activeSubNav === "page-audit") {
    const topPages: any[] = liveData?.topPages ?? [];
    const hasPageData = techSEO || topPages.length > 0;

    return (
      <div className="space-y-6">
        <DataPanel title="Page-Level Analysis" icon={<FileText className="w-4 h-4" />} actions={<WidgetExportBtn title="Page-Level Analysis" data={techSEO ? flattenMetrics({ score: techSEO.score, contentLength: techSEO.contentLength, internalLinks: techSEO.internalLinks, externalLinks: techSEO.externalLinks, hasSchema: techSEO.hasSchema }) : []} />}>
          {techSEO ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <MetricCard label="Technical Score" value={`${techSEO.score ?? 0}/100`} highlight />
                <MetricCard label="Content Length" value={`${(techSEO.contentLength ?? 0).toLocaleString()} chars`} />
                <MetricCard label="Internal Links" value={String(techSEO.internalLinks ?? 0)} />
                <MetricCard label="External Links" value={String(techSEO.externalLinks ?? 0)} />
                <MetricCard label="Images" value={techSEO.images ? `${techSEO.images.total} (${techSEO.images.missingAlt} missing alt)` : "—"} />
                <MetricCard label="Schema Markup" value={techSEO.hasSchema ? "Yes" : "No"} />
              </div>

              {techSEO.titleTag && (
                <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                  <div className="text-xs mb-2" style={{ color: "#9AA0B3" }}>Title Tag ({techSEO.titleTag.length} chars)</div>
                  <div className="text-sm" style={{ color: "#C4BDE0" }}>{techSEO.titleTag.content || "—"}</div>
                  <div className="flex gap-2 mt-2">
                    {techSEO.titleTag.present && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80" }}>Present</span>}
                    {techSEO.titleTag.optimized && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.15)", color: "#4ADE80" }}>Optimized</span>}
                  </div>
                </div>
              )}

              {techSEO.metaDescription && (
                <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                  <div className="text-xs mb-2" style={{ color: "#9AA0B3" }}>Meta Description ({techSEO.metaDescription.length} chars)</div>
                  <div className="text-sm" style={{ color: "#C4BDE0" }}>{techSEO.metaDescription.content || "—"}</div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState title="No page audit data yet" description="Run a Page-Level Audit to analyse a specific page." />
          )}
        </DataPanel>

        <DataPanel title="Top Pages by Traffic" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Top Pages" data={flattenTopPages(topPages)} />}>
          {topPages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["URL", "Keywords", "Traffic"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topPages.slice(0, 15).map((p: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2 font-mono text-xs max-w-[300px] truncate" style={{ color: "#8B7BF7" }}>{p.url}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{p.keywordsCount ?? 0}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(p.trafficSum ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !hasPageData ? (
            <EmptyState title="No top pages data yet" description="Run a Page-Level Audit to see your top-performing pages." />
          ) : null}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Technical Audit ───
  const overallScore = techSEO?.score ?? null;
  const crawlStats: { label: string; value: string }[] | null = techSEO
    ? [
        { label: "Content Length", value: techSEO.contentLength != null ? `${techSEO.contentLength}` : "—" },
        { label: "Internal Links", value: techSEO.internalLinks != null ? `${techSEO.internalLinks}` : "—" },
        { label: "External Links", value: techSEO.externalLinks != null ? `${techSEO.externalLinks}` : "—" },
        { label: "Images", value: techSEO.images != null ? `${typeof techSEO.images === "object" ? techSEO.images.total : techSEO.images}` : "—" },
      ]
    : null;

  return (
    <div className="space-y-6">
      <DataPanel title="Technical Score Breakdown" icon={<ShieldCheck className="w-4 h-4" />} actions={<WidgetExportBtn title="Technical Score" data={overallScore != null ? [{ "Overall Score": overallScore }] : []} />}>
        {overallScore != null ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { category: "Crawlability", icon: Globe },
              { category: "Indexing", icon: Bug },
              { category: "Page Speed", icon: AlertTriangle },
              { category: "Mobile Usability", icon: ShieldCheck },
            ].map((cat, i) => {
              const Icon = cat.icon;
              const catScore = i === 0 ? overallScore : null;
              return (
                <div key={i} className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4" style={{ color: "#8B7BF7" }} />
                    <span className="text-xs" style={{ color: "#9AA0B3" }}>{cat.category}</span>
                  </div>
                  {catScore != null ? (
                    <>
                      <div className="text-2xl font-bold mb-2" style={{ color: scoreColor(catScore) }}>{catScore}</div>
                      <div className="w-full h-1.5 rounded-full mb-2" style={{ background: "rgba(139,123,247,0.1)" }}>
                        <div className="h-full rounded-full" style={{ width: `${catScore}%`, background: scoreColor(catScore) }} />
                      </div>
                    </>
                  ) : (
                    <div className="text-2xl font-bold mb-2" style={{ color: "#9AA0B3" }}>—</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState title="No technical score yet" description="Run a Technical SEO or Full SEO Audit to see your technical score breakdown." />
        )}
      </DataPanel>

      <ActionPlanIssuesPanel
        cardId={cardId}
        title="Technical Issues"
        fallback={
          <DataPanel title="Technical Issues" icon={<AlertTriangle className="w-4 h-4" />} actions={<WidgetExportBtn title="Technical Issues" data={flattenTechnicalIssues(issues || [])} />}>
            {issues ? (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["ALL", "CRITICAL", "WARNINGS", "INFO"].map((label) => (
                    <button
                      key={label}
                      onClick={() => setActiveFilter(label)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{
                        background: activeFilter === label
                          ? "rgba(91,61,245,0.2)"
                          : "rgba(255,255,255,0.05)",
                        border: `1px solid ${activeFilter === label
                          ? "rgba(91,61,245,0.3)"
                          : "rgba(139,123,247,0.15)"
                          }`,
                        color: activeFilter === label ? "#8B7BF7" : "#9AA0B3",
                      }}
                    >
                      {label === "ALL" ? "All Issues" : label.charAt(0) + label.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  {filteredIssues.map((issue: any, i: number) => {
                    const colors = severityColor(issue.severity ?? "MED");
                    const normSev = (issue.severity ?? "MED").toUpperCase();
                    return (
                      <div key={i} className="flex items-start justify-between gap-3 rounded-xl px-4 py-3"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <div className="flex items-start gap-3 flex-1">
                          <div className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.text }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: "#E8E4FF" }}>{issue.issue ?? issue.title ?? issue.message ?? "Issue"}</div>
                            <div className="text-xs mt-0.5" style={{ color: "#9AA0B3" }}>{issue.detail ?? issue.description ?? ""}</div>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full font-medium flex-shrink-0"
                          style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                          {normSev === "CRITICAL" ? "HIGH" : normSev === "WARNING" ? "MED" : normSev === "INFO" ? "LOW" : normSev}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <EmptyState title="No technical issues found" description="Run a Technical SEO or Full SEO Audit to identify issues on your site." />
            )}
          </DataPanel>
        }
      />

      <DataPanel title="Crawl Statistics" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Crawl Statistics" data={crawlStats ? flattenMetrics(Object.fromEntries(
        crawlStats.map(item => [item.label, item.value])
      )) : []} />}>
        {crawlStats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {crawlStats.map((stat, i) => (
              <MetricCard key={i} label={stat.label} value={stat.value} />
            ))}
          </div>
        ) : (
          <EmptyState title="No crawl data yet" description="Run a Technical SEO or Full SEO Audit to see crawl statistics." />
        )}
      </DataPanel>
    </div>
  );
};

export default TechnicalTabContent;
